import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useCallAPI } from 'hooks/useCallAPI';
import { Dayparts } from 'core/dayparts/Dayparts';
import {
  DeviceAvailabilityCalendarPoint,
  DeviceBusinessHoursProvenance
} from 'core/deviceManagement/DeviceManagement';
import {
  DefaultDeviceManagementManager,
  DeviceManagementManager
} from 'core/deviceManagement/DeviceManagementManager';
import {
  AVAILABILITY_LEGEND,
  AVAILABILITY_LEVEL_I18N,
  availabilityRateClassName,
  buildMonthGrid,
  isDateInMonth,
  monthsInRange,
  resolveAvailabilityLevel
} from './availabilityCalendar';
import { DAYPART_DAYS, toDaypartsParam } from './availabilityDayparts';
import { NO_VALUE } from './deviceDetail';
import {
  countDaysInRange,
  formatAvailabilityRate,
  hasResolvedBusinessHours
} from './availabilityFormat';
import styles from './deviceManagement.module.scss';

type AvailabilityCalendarPanelProps = {
  channelId: number;
  unitKey: string;
  businessHoursProvenance: DeviceBusinessHoursProvenance;
  startDate: string;
  endDate: string;
  dayparts: Dayparts;
  /**
   * 整段走期的可售率。用清單那一列已經拿到的值，而不是把月曆的逐日資料再加總
   * 一次：兩者是同一組走期與時段算出來的，各算一份就會出現兩個答案。
   */
  availabilityRate: number | null;
  manager?: DeviceManagementManager;
};

// 星期文案用 daypart 那一組現成的，不另外開一份：同一個字在同一個產品裡有兩份
// 文案，兩邊遲早會漂開，而 daypart 那組三個語系都已經翻好。
const WEEKDAY_I18N = [
  'daypart.labels.monday',
  'daypart.labels.tuesday',
  'daypart.labels.wednesday',
  'daypart.labels.thursday',
  'daypart.labels.friday',
  'daypart.labels.saturday',
  'daypart.labels.sunday'
];

const defaultManager = new DefaultDeviceManagementManager();

/**
 * 未來可售狀況月曆。
 *
 * 沒有營業時段資料的點位不畫色階，整塊換成一句說明。營業時段是點位層的欄位，
 * 跟哪一天無關，所以那種點位的月曆必然整面同一種狀態：整面留白分不出是壞了還是
 * 沒資料，整面灰白又會跟「非投放時段」看起來一樣，而那是另一件事。
 */
export const AvailabilityCalendarPanel: React.FC<
  AvailabilityCalendarPanelProps
> = ({
  channelId,
  unitKey,
  businessHoursProvenance,
  startDate,
  endDate,
  dayparts,
  availabilityRate,
  manager = defaultManager
}) => {
  const { loading, callAPIs } = useCallAPI();
  const [points, setPoints] = useState<DeviceAvailabilityCalendarPoint[]>([]);
  const [failed, setFailed] = useState(false);
  const [monthIndex, setMonthIndex] = useState(0);

  const resolved = hasResolvedBusinessHours(businessHoursProvenance);
  const months = useMemo(
    () => monthsInRange(startDate, endDate),
    [startDate, endDate]
  );

  // 換了點位或走期，原本停在第幾個月就沒有意義了。
  useEffect(() => {
    setMonthIndex(0);
  }, [unitKey, startDate, endDate]);

  useEffect(() => {
    if (!resolved) {
      return;
    }
    callAPIs(
      [
        () =>
          manager.getAvailabilityCalendar({
            channelId,
            unitKey,
            startDate,
            endDate,
            dayparts: toDaypartsParam(dayparts)
          })
      ],
      (result: DeviceAvailabilityCalendarPoint[]) => {
        setFailed(false);
        setPoints(result);
      },
      () => {
        // 沒有這一段的話，失敗與「整段走期都不在投放時段內」在畫面上長得一模一樣：
        // 沒有資料點的日子會被畫成「非投放時段」，整個月曆因此變成一個看起來很肯定的
        // 錯誤答案。這裡把點清掉並改顯示載入失敗。
        setFailed(true);
        setPoints([]);
      }
    );
  }, [
    resolved,
    channelId,
    unitKey,
    startDate,
    endDate,
    dayparts,
    callAPIs,
    manager
  ]);

  const pointsByDate = useMemo(
    () =>
      points.reduce<Record<string, DeviceAvailabilityCalendarPoint>>(
        (bag, point) => {
          bag[point.date] = point;
          return bag;
        },
        {}
      ),
    [points]
  );

  const month = months[Math.min(monthIndex, months.length - 1)];
  const weeks = useMemo(() => buildMonthGrid(month), [month]);

  const onPreviousMonth = useCallback(
    () => setMonthIndex((index) => Math.max(0, index - 1)),
    []
  );
  const onNextMonth = useCallback(
    () => setMonthIndex((index) => Math.min(months.length - 1, index + 1)),
    [months.length]
  );

  return (
    <section className={styles.detailSection}>
      <h2>{i18n.t('deviceManagement.labels.availabilityCalendar')}</h2>
      <div className={styles.calendarRange} data-testid='calendar-range'>
        {i18n.t('deviceManagement.labels.calendarRange', {
          startDate,
          endDate,
          days: countDaysInRange(startDate, endDate)
        })}
      </div>
      {!resolved || failed ? (
        <div
          className={styles.calendarNoData}
          data-testid='availability-calendar-no-data'
        >
          {i18n.t(
            failed
              ? 'deviceManagement.messages.calendarLoadFailed'
              : 'deviceManagement.messages.calendarNoBusinessHours'
          )}
        </div>
      ) : (
        <>
          {loading && <LoadingIndicator />}
          <div className={styles.calendarRate}>
            <span className={styles.calendarRateLabel}>
              {i18n.t('deviceManagement.labels.overallAvailabilityRate')}
            </span>
            <span
              className={
                availabilityRate === null
                  ? styles.calendarRateValue
                  : `${styles.calendarRateValue} ${availabilityRateClassName(availabilityRate)}`
              }
              data-testid='calendar-availability-rate'
            >
              {availabilityRate === null
                ? NO_VALUE
                : formatAvailabilityRate(availabilityRate)}
            </span>
          </div>
          <div className={styles.calendarHeader}>
            <button
              type='button'
              className={styles.calendarNav}
              disabled={monthIndex === 0}
              onClick={onPreviousMonth}
              aria-label={i18n.t('deviceManagement.buttons.previousMonth')}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <span
              className={styles.calendarMonth}
              data-testid='availability-calendar-month'
            >
              {month}
            </span>
            <button
              type='button'
              className={styles.calendarNav}
              disabled={monthIndex >= months.length - 1}
              onClick={onNextMonth}
              aria-label={i18n.t('deviceManagement.buttons.nextMonth')}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
            <span
              className={styles.calendarMonthPosition}
              data-testid='availability-calendar-month-position'
            >
              {`${Math.min(monthIndex, months.length - 1) + 1} / ${months.length}`}
            </span>
          </div>
          <div
            className={styles.calendarGrid}
            data-testid='availability-calendar-grid'
          >
            {DAYPART_DAYS.map((day) => (
              <div
                key={`weekday-${day}`}
                className={styles.calendarWeekday}
                data-testid='calendar-weekday'
              >
                {i18n.t(WEEKDAY_I18N[day - 1])}
              </div>
            ))}
            {weeks.map((week) =>
              week.map((date) => {
                const level = resolveAvailabilityLevel(
                  date,
                  pointsByDate[date],
                  { startDate, endDate, dayparts }
                );
                const classNames = [styles.calendarDay, styles[level]];
                if (!isDateInMonth(date, month)) {
                  classNames.push(styles.calendarOtherMonth);
                }
                return (
                  <div
                    key={date}
                    className={classNames.join(' ')}
                    data-testid={`calendar-day-${date}`}
                    data-level={level}
                    title={i18n.t(AVAILABILITY_LEVEL_I18N[level])}
                  >
                    {Number(date.slice(8, 10))}
                  </div>
                );
              })
            )}
          </div>
          <div className={styles.calendarLegend}>
            {AVAILABILITY_LEGEND.map((level) => (
              <span key={level} className={styles.calendarLegendItem}>
                <i
                  className={`${styles.calendarLegendSwatch} ${styles[level]}`}
                />
                {i18n.t(AVAILABILITY_LEVEL_I18N[level])}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
};
